import { ApplicationConfig, LOCALE_ID, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection } from '@angular/core';
import { registerLocaleData } from '@angular/common';
import localePl from '@angular/common/locales/pl';
import { provideRouter } from '@angular/router';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { MessageService } from 'primeng/api';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import { provideMapPicker } from '@konradkrk/ngx-map-picker';

import { routes } from './app.routes';
import { provideTranslocoConfig } from './core/i18n';
import { provideWhiteLabel } from './core/white-label/white-label.initializer';
import { DeviceHeaderInterceptor } from './core/interceptors/device-header.interceptor';

registerLocaleData(localePl);

const KolorovePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#fff7ed',
      100: '#ffedd5',
      200: '#fed7aa',
      300: '#fdba74',
      400: '#fb923c',
      500: '#f97316',
      600: '#ea580c',
      700: '#c2410c',
      800: '#9a3412',
      900: '#7c2d12',
      950: '#431407'
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '#f9fafb',
          100: '#f3f4f6',
          200: '#e5e7eb',
          300: '#d1d5db',
          400: '#9ca3af',
          500: '#6b7280',
          600: '#4b5563',
          700: '#374151',
          800: '#1f2937',
          900: '#111827',
          950: '#030712'
        }
      }
    }
  }
});

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: KolorovePreset,
        options: {
          darkModeSelector: 'none'
        }
      },
      ripple: true,
      translation: {
        firstDayOfWeek: 1,
        dayNames: ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'],
        dayNamesShort: ['Nie', 'Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob'],
        dayNamesMin: ['Nd', 'Pn', 'Wt', 'Śr', 'Cz', 'Pt', 'Sb'],
        monthNames: ['Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec', 'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'],
        monthNamesShort: ['Sty', 'Lut', 'Mar', 'Kwi', 'Maj', 'Cze', 'Lip', 'Sie', 'Wrz', 'Paź', 'Lis', 'Gru'],
        today: 'Dziś',
        clear: 'Wyczyść',
        weekHeader: 'Tydz',
        dateFormat: 'dd.mm.yy',
      },
      pt: {
        card: {
          footer: {
            style: {
              padding: '0'
            }
          }
        }
      }
    }),
    provideMapPicker({
      defaultCenter: { lat: 54.35, lng: 18.65 }, // Polska - Gdańsk
      defaultZoom: 12,
      geocoding: {
        language: 'pl',
        countryCodes: ['pl'],
        debounceTime: 500,
      },
    }),
    ...provideTranslocoConfig(),
    ...provideWhiteLabel(),
    MessageService,
    { provide: HTTP_INTERCEPTORS, useClass: DeviceHeaderInterceptor, multi: true },
    { provide: LOCALE_ID, useValue: 'pl' }
  ]
};
